import { useCallback } from 'react';
import { bridge, getNumberRange, isNil } from 'shared/src/utils';
import { useGoodsDetail } from '../queries/useGoodsDetail';
import { useGoodsBasic } from '../queries/useGoodsBasic';

const SCREEN = 'GOODS_DETAIL';

export const useLogGoodsDefaultAnalyticsParams = () => {
  const { data: goodsDetail } = useGoodsDetail();
  const { data: goodsBasic } = useGoodsBasic();

  return useCallback(() => {
    if (isNil(goodsDetail)) return false;
    if (isNil(goodsBasic)) return false;

    const categories = goodsBasic.display_categories?.[0] ?? null;

    return {
      SCREEN,
      CATEGORY_DEPTH: categories?.depth,
      CATEGORY_SNO: categories?.sno.toString(),
      CATEGORY_NAME: categories?.name,
      DELIVERY_LEAD_TIME:
        goodsBasic.delivery_type_info.delivery_type === 'standard'
          ? goodsDetail.goods.send_expectation_info?.days
          : null,
      DELIVERY_TYPE: goodsBasic.delivery_type_info.delivery_type,
      DISCOUNT_RATE: goodsBasic.price_info.discount_rate,
      GOODS_SNO: goodsBasic.sno.toString(),
      GOODS_NAME: goodsBasic.name,
      // IS_AD: '0',
      IS_NEW: goodsBasic.is_new ? '1' : '0',
      LIKES_COUNT: goodsDetail.goods.score.likes_count,
      LIKES_COUNT_RANGE: getNumberRange({
        rangeUnit: 500,
        target: goodsDetail.goods.score.likes_count,
      }),
      MARKET_SNO: goodsBasic.market.sno.toString(),
      MARKET_NAME: goodsBasic.market.name,
      MARKET_TYPE: goodsBasic.market.market_vertical_type.name,
      ORIGINAL_PRICE: goodsBasic.price_info.consumer,
      REVIEW_COUNT: goodsDetail.goods.score.total_review_count,
      REVIEW_COUNT_RANGE: getNumberRange({
        target: goodsDetail.goods.score.total_review_count,
        rangeUnit: 50,
      }), //1~50
      REVIEW_RATING: goodsBasic.positive_review_rate,
      SALES_PRICE: goodsBasic.price_info.thumbnail_price,
      SALES_PRICE_RANGE: getNumberRange({
        target: goodsBasic.price_info.thumbnail_price,
        rangeUnit: 5000,
      }), //20,001~25,000,
      STANDARD_CATEGORY_SNO: goodsBasic.standard_category.sno.toString(),
      STANDARD_CATEGORY_NAME: goodsBasic.standard_category.name,

      ...goodsDetail.logging.analytics,
    };
  }, [goodsDetail, goodsBasic]);
};

export const useLogGoodsAnalyticsEvent = () => {
  const { data: goodsDetail } = useGoodsDetail();
  const { data: goodsBasic } = useGoodsBasic();

  const getDefaultAnalyticsParams = useLogGoodsDefaultAnalyticsParams();

  return useCallback<
    (params: {
      name: string;
      channel?: Array<
        | 'firebase'
        | 'amplitude'
        | 'braze'
        | 'ablylog'
        | 'facebook'
        | 'airbridge'
      >;
      analytics?: Record<string, unknown>;
    }) => boolean
  >(
    params => {
      if (isNil(goodsDetail)) return false;
      if (isNil(goodsBasic)) return false;

      bridge.logAnalyticsEvent({
        name: params.name,
        channel: params.channel ?? [
          'firebase',
          'facebook',
          'amplitude',
          'braze',
          'ablylog',
        ],
        analytics: {
          SCREEN,
          ...getDefaultAnalyticsParams(),
          ...params.analytics,
          EVENT_TYPE: params.name,
        },
      });

      return true;
    },
    [getDefaultAnalyticsParams, goodsBasic, goodsDetail],
  );
};
